import { useEffect, useRef, lazy, Suspense } from "react";
import {
  BookOpen, Download, Shield, Smartphone, Lock, Star,
  ChevronRight, Gift, Music, Flame, Heart, Sparkles,
  Users, BookMarked, Globe, Brain, Scale, Compass, Search, MessageCircle
} from "lucide-react";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent
} from "@/components/ui/accordion";
import {
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell
} from "@/components/ui/table";
import CountdownTimer from "@/components/CountdownTimer";

const StickyBuyButton = lazy(() => import("@/components/StickyBuyButton"));
const SocialProofPopup = lazy(() => import("@/components/SocialProofPopup"));
const ExitIntentPopup = lazy(() => import("@/components/ExitIntentPopup"));

const CTA_LINK = "https://superprofile.bio/vp/6958efc5bfbbf90013a9338e";
const HERO_IMAGE_SM = "https://imgproxy.gamma.app/resize/quality:70/resizing_type:fit/width:480/https://cdn.gamma.app/crhx0nczn9l6a3t/250b7326d0a34eacb7cb88161d8266a8/original/Kannada-ved-aur-puraann-grNth-1080-x-1080-px.png";
const HERO_IMAGE_MD = "https://imgproxy.gamma.app/resize/quality:75/resizing_type:fit/width:768/https://cdn.gamma.app/crhx0nczn9l6a3t/250b7326d0a34eacb7cb88161d8266a8/original/Kannada-ved-aur-puraann-grNth-1080-x-1080-px.png";
const HERO_IMAGE_LG = "https://imgproxy.gamma.app/resize/quality:80/resizing_type:fit/width:1024/https://cdn.gamma.app/crhx0nczn9l6a3t/250b7326d0a34eacb7cb88161d8266a8/original/Kannada-ved-aur-puraann-grNth-1080-x-1080-px.png";
const PROBLEMS_IMAGE = "https://imgproxy.gamma.app/resize/quality:80/resizing_type:fit/width:2000/https://cdn.gamma.app/crhx0nczn9l6a3t/generated-images/a0zaGas6IYuE9oo0o0fYP.png";
const AUDIENCE_IMAGE = "https://imgproxy.gamma.app/resize/quality:80/resizing_type:fit/width:2000/https://cdn.gamma.app/crhx0nczn9l6a3t/generated-images/XECNiC6bf92ggLv4XXcce.png";

const VEDAS = [
  { name: "ಋಗ್ವೇದ", desc: "ದೇವರನ್ನ ಹೊಗಳೋ ಸ್ತೋತ್ರಗಳು, ಪ್ರಾರ್ಥನೆಗಳ ಸಂಗ್ರಹ. ನಮ್ಮ ಅತ್ಯಂತ ಹಳೆಯ ಗ್ರಂಥ." },
  { name: "ಯಜುರ್ವೇದ", desc: "ಪೂಜೆ, ಹವನ, ಯಜ್ಞ ಮಾಡೋದು ಹೇಗೆ ಅಂತ ವಿವರವಾಗಿ ಹೇಳುತ್ತೆ." },
  { name: "ಸಾಮವೇದ", desc: "ಸಂಗೀತ ರೂಪದಲ್ಲಿ ಮಂತ್ರಗಳು – ಕೇಳಿದ್ರೆ ಮನಸ್ಸಿಗೆ ಶಾಂತಿ ಸಿಗುತ್ತೆ." },
  { name: "ಅಥರ್ವವೇದ", desc: "ದಿನನಿತ್ಯ ಬದುಕಿಗೆ ಬೇಕಾದ ಜ್ಞಾನ – ಆರೋಗ್ಯ, ಔಷಧ, ಜೀವನ ಸೂತ್ರಗಳು." },
];

const PURANAS = [
  { name: "ಬ್ರಹ್ಮ ಪುರಾಣ", desc: "ಜಗತ್ತು ಹೇಗೆ ಹುಟ್ಟಿತು? ಬ್ರಹ್ಮ ದೇವರ ಕಥೆಗಳು." },
  { name: "ಪದ್ಮ ಪುರಾಣ", desc: "ಪುಣ್ಯ ಕ್ಷೇತ್ರಗಳ ಮಹಿಮೆ, ದೇವರ ಶಕ್ತಿಯ ಕಥೆಗಳು." },
  { name: "ವಿಷ್ಣು ಪುರಾಣ", desc: "ವಿಷ್ಣು ಭಗವಂತನ 10 ಅವತಾರಗಳ ರೋಚಕ ಕಥೆಗಳು." },
  { name: "ಶಿವ ಪುರಾಣ", desc: "ಮಹಾದೇವ ಶಿವನ ಮಹಿಮೆ, ಪವಾಡಗಳು, ಭಕ್ತರ ಕಥೆಗಳು." },
  { name: "ಭಾಗವತ ಪುರಾಣ", desc: "ಕೃಷ್ಣನ ಬಾಲಲೀಲೆ, ಗೋಪಿಕೆಯರ ಭಕ್ತಿ – ಓದಿದ್ರೆ ಕಣ್ಣೀರು ಬರುತ್ತೆ." },
  { name: "ನಾರದ ಪುರಾಣ", desc: "ನಾರದ ಮುನಿ ಹೇಳಿದ ಭಕ್ತಿ ಮಾರ್ಗ, ಧರ್ಮ, ನೀತಿ ಕಥೆಗಳು." },
  { name: "ಮಾರ್ಕಂಡೇಯ ಪುರಾಣ", desc: "ದುರ್ಗಾ ದೇವಿಯ ಶಕ್ತಿ, ದುಷ್ಟರ ಸಂಹಾರದ ಕಥೆಗಳು." },
  { name: "ಅಗ್ನಿ ಪುರಾಣ", desc: "ಜ್ಯೋತಿಷ್ಯ, ವಾಸ್ತು, ಯುದ್ಧಕಲೆ – ಎಲ್ಲಾ ವಿಷಯ ಒಂದೇ ಕಡೆ." },
  { name: "ಭವಿಷ್ಯೋತ್ತರ ಪುರಾಣ", desc: "ಮುಂದೆ ಏನಾಗುತ್ತೆ? ಭವಿಷ್ಯದ ಘಟನೆಗಳ ಬಗ್ಗೆ ಮಾಹಿತಿ." },
  { name: "ಬ್ರಹ್ಮ ವೈವರ್ತ ಪುರಾಣ", desc: "ರಾಧಾ-ಕೃಷ್ಣರ ಪ್ರೇಮ ಕಥೆ, ಸೃಷ್ಟಿಯ ರಹಸ್ಯಗಳು." },
  { name: "ಲಿಂಗ ಪುರಾಣ", desc: "ಶಿವಲಿಂಗದ ಮಹಿಮೆ, ಪೂಜಾ ವಿಧಾನ, ಶಿವ ಭಕ್ತಿ." },
  { name: "ವರಾಹ ಪುರಾಣ", desc: "ವಿಷ್ಣು ಹಂದಿ ರೂಪ ತೊಟ್ಟು ಭೂಮಿಯನ್ನ ಉಳಿಸಿದ ಕಥೆ." },
  { name: "ಸ್ಕಂದ ಪುರಾಣ", desc: "ಅತ್ಯಂತ ದೊಡ್ಡ ಪುರಾಣ – ತೀರ್ಥ ಯಾತ್ರೆ, ದೇವಸ್ಥಾನಗಳ ಮಹಿಮೆ." },
  { name: "ವಾಮನ ಪುರಾಣ", desc: "ಚಿಕ್ಕ ಬ್ರಾಹ್ಮಣ ರೂಪದಲ್ಲಿ ಬಂದ ವಿಷ್ಣು, ಬಲಿ ಚಕ್ರವರ್ತಿ ಕಥೆ." },
  { name: "ಕೂರ್ಮ ಪುರಾಣ", desc: "ಆಮೆ ರೂಪದ ವಿಷ್ಣು, ಸಮುದ್ರ ಮಂಥನದ ರೋಮಾಂಚಕ ಕಥೆ." },
  { name: "ಮತ್ಸ್ಯ ಪುರಾಣ", desc: "ಮೀನು ರೂಪದ ವಿಷ್ಣು ಜಗತ್ತನ್ನ ಪ್ರಳಯದಿಂದ ಕಾಪಾಡಿದ ಕಥೆ." },
  { name: "ಗರುಡ ಪುರಾಣ", desc: "ಸತ್ತ ಮೇಲೆ ಏನಾಗುತ್ತೆ? ಪರಲೋಕ, ಕರ್ಮಫಲದ ಬಗ್ಗೆ." },
  { name: "ಬ್ರಹ್ಮಾಂಡ ಪುರಾಣ", desc: "ಈ ಬ್ರಹ್ಮಾಂಡ ಹೇಗೆ ಹುಟ್ಟಿತು? ಯುಗಗಳ ಕಥೆ, ಕಾಲಚಕ್ರ." },
];

const FEATURES = [
  { icon: BookOpen, title: "4 ವೇದಗಳು", desc: "ಋಗ್ವೇದ, ಯಜುರ್ವೇದ, ಸಾಮವೇದ, ಅಥರ್ವವೇದ – ಎಲ್ಲಾ ಸರಳ ಕನ್ನಡದಲ್ಲಿ" },
  { icon: Flame, title: "18 ಪುರಾಣಗಳು", desc: "ಶಿವ, ವಿಷ್ಣು, ಭಾಗವತ ಪುರಾಣ ಸೇರಿ ಎಲ್ಲಾ 18 ಪುರಾಣಗಳು" },
  { icon: Sparkles, title: "ಉಪನಿಷತ್ತುಗಳು", desc: "ಜೀವನದ ರಹಸ್ಯ ಏನು? ಬದುಕಿನ ಅರ್ಥ ಏನು? ಎಲ್ಲಾ ಉತ್ತರ ಇಲ್ಲಿದೆ" },
  { icon: Heart, title: "ಸಂಸ್ಕೃತಿ ಮತ್ತು ಆಚಾರ", desc: "ಹಬ್ಬ ಹೇಗೆ ಮಾಡಬೇಕು? ಪೂಜೆ ಹೇಗೆ? ಎಲ್ಲಾ ಸಂಸ್ಕಾರಗಳು" },
  { icon: Gift, title: "FREE ಬೋನಸ್: ವಾಸ್ತು ಬುಕ್", desc: "ಮನೆ ಸರಿ ಇದ್ರೆ ಮನಸ್ಸೂ ಸರಿ – ವಾಸ್ತು ಟಿಪ್ಸ್ FREE" },
  { icon: Music, title: "FREE ಬೋನಸ್: ಯೋಗ PDF", desc: "ಒಳಗಿನಿಂದ ಆಯುರ್ವೇದ, ಹೊರಗಿನಿಂದ ಯೋಗ – ದೇಹ ಫಿಟ್ ಆಗಲಿ" },
];

const UNIQUE_FEATURES = [
  { icon: BookMarked, title: "ಎಲ್ಲಾ ಒಂದೇ ಕಡೆ", desc: "4 ವೇದ + 18 ಪುರಾಣ ಎಲ್ಲಾ ಒಂದೇ ಸಂಗ್ರಹದಲ್ಲಿ. ಅಲ್ಲಿ ಇಲ್ಲಿ ಹುಡುಕೋ ಕೆಲಸ ಇಲ್ಲ, ಎಲ್ಲಾ ರೆಡಿ ಇದೆ." },
  { icon: Globe, title: "ಸುಲಭ ಕನ್ನಡ", desc: "ಕಷ್ಟದ ಸಂಸ್ಕೃತ ಇಲ್ಲ. ನಮ್ಮ ಅಜ್ಜ ಅಜ್ಜಿ ಹೇಳಿದ ಹಾಗೆ ಸುಲಭ ಕನ್ನಡದಲ್ಲಿ ಬರೆದಿದ್ದೇವೆ. ಯಾರಿಗಾದ್ರೂ ಅರ್ಥ ಆಗುತ್ತೆ." },
  { icon: Smartphone, title: "ಫೋನ್‌ನಲ್ಲೇ ಓದಿ", desc: "ಬಸ್‌ನಲ್ಲಿ, ಆಫೀಸ್ ಬ್ರೇಕ್‌ನಲ್ಲಿ, ರಾತ್ರಿ ಮಲಗೋ ಮುಂಚೆ – ಯಾವಾಗ ಬೇಕಾದ್ರೂ, ಎಲ್ಲಿ ಬೇಕಾದ್ರೂ ಫೋನ್‌ನಲ್ಲೇ ಓದಿ." },
  { icon: Shield, title: "ಒಂದೇ ಸಲ ₹199, ಮುಗೀತು", desc: "ಒಂದು ಸಲ ₹199 ಕೊಟ್ಟರೆ ಜೀವನಪೂರ್ತಿ ನಿಮ್ಮದೇ. ಮತ್ತೆ ಯಾವ ಚಾರ್ಜ್ ಇಲ್ಲ, ಯಾವ ಸಬ್‌ಸ್ಕ್ರಿಪ್ಷನ್ ಇಲ್ಲ." },
];

const PROBLEMS = [
  { icon: BookOpen, title: "ಸಂಸ್ಕೃತ ಅರ್ಥ ಆಗಲ್ಲ?", desc: "ವೇದ-ಪುರಾಣ ಓದಬೇಕು ಅಂತಿದೆ ಆದ್ರೆ ಸಂಸ್ಕೃತ ಅರ್ಥ ಆಗಲ್ಲ ಅಂತಿದ್ರಾ? ನಮ್ಮ ಸುಲಭ ಕನ್ನಡ ಅನುವಾದ ಇದ್ರೆ ಯಾರಿಗಾದ್ರೂ ಅರ್ಥ ಆಗುತ್ತೆ." },
  { icon: Search, title: "ಒರಿಜಿನಲ್ ಬುಕ್ಸ್ ಸಿಗಲ್ಲ?", desc: "ಊರಲ್ಲಿ, ಸಣ್ಣ ಟೌನ್‌ನಲ್ಲಿ ಒಳ್ಳೆಯ ಧಾರ್ಮಿಕ ಪುಸ್ತಕ ಸಿಗೋದೇ ಕಷ್ಟ. ನಮ್ಮ ಡಿಜಿಟಲ್ ಕಲೆಕ್ಷನ್ ಎಲ್ಲಿಂದ ಬೇಕಾದ್ರೂ ಡೌನ್‌ಲೋಡ್ ಮಾಡಿ." },
  { icon: Compass, title: "ಯಾರು ಹೇಳಿಕೊಡ್ತಾರೆ?", desc: "ಗುರು ಇಲ್ಲ, ಪಂಡಿತರ ಬಳಿ ಹೋಗೋಕೆ ಟೈಮ್ ಇಲ್ಲ. ನಮ್ಮ ಸರಳ ವಿವರಣೆ ಒಬ್ಬ ಗುರು ಪಕ್ಕ ಕುಳಿತು ಹೇಳಿದ ಹಾಗೆ ಇರುತ್ತೆ." },
];

const BENEFITS = [
  { icon: Heart, title: "ಮನಸ್ಸಿಗೆ ನೆಮ್ಮದಿ", desc: "ಟೆನ್ಶನ್ ಜೀವನದಲ್ಲಿ ಸ್ವಲ್ಪ ಶಾಂತಿ ಬೇಕಾ? ಪುರಾಣ ಕಥೆಗಳು ಓದಿದ್ರೆ ಮನಸ್ಸಿಗೆ ತಂಪು ಸಿಗುತ್ತೆ." },
  { icon: BookOpen, title: "ಪೂಜೆ-ಹಬ್ಬ ಸರಿಯಾಗಿ ಮಾಡಿ", desc: "ಯಾವ ಪೂಜೆ ಹೇಗೆ ಮಾಡಬೇಕು? ವ್ರತ, ಹಬ್ಬಗಳ ಸರಿಯಾದ ವಿಧಾನ ಗೊತ್ತಾಗುತ್ತೆ." },
  { icon: Users, title: "ಮಕ್ಕಳಿಗೆ ಕಲಿಸಿ", desc: "ಮಕ್ಕಳು ಕೇಳ್ತಾರೆ – 'ರಾಮಾಯಣ ಏನಪ್ಪ?' ಅಂತ. ಈಗ ನೀವೇ ಕಥೆ ಹೇಳಬಹುದು. ಮಕ್ಕಳಿಗೆ ನಮ್ಮ ಸಂಸ್ಕೃತಿ ಬಗ್ಗೆ ಹೆಮ್ಮೆ ಹುಟ್ಟುತ್ತೆ." },
  { icon: Brain, title: "ಪಾಸಿಟಿವ್ ಎನರ್ಜಿ", desc: "ಪ್ರತಿ ದಿನ ಸ್ವಲ್ಪ ಓದಿದ್ರೆ ಸಾಕು – ಮನಸ್ಸಿನಲ್ಲಿ ಒಳ್ಳೆಯ ವಿಚಾರಗಳು ಬರುತ್ವೆ. ಲೈಫ್‌ನಲ್ಲಿ ಪಾಸಿಟಿವ್ ಎನರ್ಜಿ ಹೆಚ್ಚಾಗುತ್ತೆ." },
  { icon: Scale, title: "ಒಳ್ಳೆಯ ಮನುಷ್ಯ ಆಗಿ", desc: "ಕಥೆಗಳಿಂದ ಬದುಕಿನ ಪಾಠ ಕಲಿಯಿರಿ. ಸರಿ-ತಪ್ಪು ಏನು ಅಂತ ಅರ್ಥ ಆಗುತ್ತೆ. ಕುಟುಂಬದಲ್ಲಿ ಗೌರವ ಹೆಚ್ಚಾಗುತ್ತೆ." },
];

const TARGET_AUDIENCE = [
  { icon: Users, title: "ಕುಟುಂಬಸ್ಥ ಪುರುಷರು", desc: "ಮಕ್ಕಳಿಗೆ ನಮ್ಮ ಸಂಸ್ಕೃತಿ ಬಗ್ಗೆ ಹೇಳಬೇಕು ಅಂತಿದ್ರಾ? ಪೂಜೆ ವಿಧಾನ ಸರಿಯಾಗಿ ತಿಳಿಯಬೇಕಾ? ಈ ಸಂಗ್ರಹ ನಿಮಗೇ." },
  { icon: Heart, title: "ಭಕ್ತಿ ಇರೋರು", desc: "ದೇವಸ್ಥಾನಕ್ಕೆ ಹೋಗ್ತೀರಾ, ಪೂಜೆ ಮಾಡ್ತೀರಾ – ಆದ್ರೆ ಆಳವಾಗಿ ತಿಳಿಯಬೇಕಾ? ಈ ಗ್ರಂಥಗಳು ನಿಮ್ಮ ಭಕ್ತಿಗೆ ಜ್ಞಾನದ ಬಲ ಕೊಡುತ್ತವೆ." },
  { icon: BookOpen, title: "ಊರಿನ ಹಿರಿಯರು", desc: "ಊರಿನಲ್ಲಿ ಜನ ನಿಮ್ಮನ್ನ ಕೇಳ್ತಾರೆ, ಆಚರಣೆ ಬಗ್ಗೆ ವಿಚಾರಿಸ್ತಾರೆ. ಈ ಜ್ಞಾನ ಇದ್ರೆ ನೀವು ಎಲ್ಲರಿಗೂ ಸರಿಯಾಗಿ ಹೇಳಬಹುದು." },
];

const TESTIMONIALS = [
  { name: "ರಾಮಚಂದ್ರ ಶೆಟ್ಟಿ", age: "52", city: "ಮಂಗಳೂರು", text: "ವೇದ ಅಂದ್ರೆ ಕಷ್ಟ ಅಂದುಕೊಂಡಿದ್ದೆ. ಆದ್ರೆ ಇದನ್ನ ಓದಿದಾಗ ಒಬ್ಬ ಗುರು ಪಕ್ಕ ಕುಳಿತು ಹೇಳಿಕೊಡ್ತಿದ್ದಾರೆ ಅಂತ ಅನ್ನಿಸ್ತು. ₹199ಗೆ ಇಷ್ಟೆಲ್ಲಾ ಸಿಗುತ್ತಾ ಅಂತ ನಂಬಲೇ ಆಗಲಿಲ್ಲ!" },
  { name: "ವಿನಯ ಗೌಡ", age: "48", city: "ಮೈಸೂರು", text: "ನಾನು ಮೊದಲು ಮಕ್ಕಳಿಗೆ ನಮ್ಮ ಕಥೆಗಳು ಹೇಳಕ್ಕೆ ಆಗ್ತಿರಲಿಲ್ಲ. ಈಗ ಪ್ರತಿ ರಾತ್ರಿ ಒಂದೊಂದು ಕಥೆ ಹೇಳ್ತೀನಿ. ಮಕ್ಕಳಿಗೆ ತುಂಬಾ ಇಷ್ಟ ಆಗಿದೆ!" },
  { name: "ಶಿವಕುಮಾರ ನಾಯಕ", age: "50", city: "ಹುಬ್ಬಳ್ಳಿ", text: "ನಮ್ಮ ಮನೇಲಿ ಪ್ರತಿ ಭಾನುವಾರ ಒಟ್ಟಿಗೆ ಓದೋ ಹೊಸ ಸಂಪ್ರದಾಯ ಶುರು ಆಯ್ತು. ಹೆಂಡ್ತಿ ಮಕ್ಕಳೆಲ್ಲ ಸೇರಿ ಓದ್ತೀವಿ. ಬೆಸ್ಟ್ ₹199 ಖರ್ಚು!" },
  { name: "ಮಹೇಶ ಪಾಟೀಲ", age: "47", city: "ಬೆಳಗಾವಿ", text: "ಒಂದು ಕಾಫಿ ಬೆಲೆಗೆ ಇಡೀ ಸನಾತನ ಜ್ಞಾನ ಸಿಕ್ಕಿತು. ಓದುವಾಗ ಗುರುಕುಲದಲ್ಲಿ ಕುಳಿತಿದ್ದೇನೆ ಅಂತ ಅನ್ನಿಸ್ತು. ಲೈಫ್‌ನ ಬೆಸ್ಟ್ ಇನ್ವೆಸ್ಟ್ಮೆಂಟ್!" },
];

const FAQS = [
  { q: "ಕನ್ನಡದಲ್ಲಿ ಇದೆಯಾ? ಅರ್ಥ ಆಗುತ್ತಾ?", a: "ಹೌದು! ಎಲ್ಲಾ ವೇದ-ಪುರಾಣಗಳು ಸರಳ, ಸುಲಭ ಕನ್ನಡದಲ್ಲಿ ಇವೆ. ಕಷ್ಟದ ಸಂಸ್ಕೃತ ಇಲ್ಲ. ಯಾರಿಗಾದ್ರೂ ಓದಿ ಅರ್ಥ ಆಗುವ ಹಾಗೆ ಬರೆದಿದ್ದೇವೆ." },
  { q: "ಡೌನ್\u200Cಲೋಡ್ ತಕ್ಷಣ ಸಿಗುತ್ತದೆಯೇ?", a: "ಹೌದು! ಪೇಮೆಂಟ್ ಆದ 5 ನಿಮಿಷದಲ್ಲಿ ನಿಮ್ಮ WhatsApp ಅಥವಾ Email ಗೆ ಡೌನ್\u200Cಲೋಡ್ ಲಿಂಕ್ ಬರುತ್ತದೆ. ತಕ್ಷಣ ಓದಲು ಶುರು ಮಾಡಬಹುದು." },
  { q: "ಮೊಬೈಲ್\u200Cನಲ್ಲಿ ಓದಬಹುದಾ?", a: "ಖಂಡಿತ! ಮೊಬೈಲ್, ಟ್ಯಾಬ್ಲೆಟ್, ಕಂಪ್ಯೂಟರ್ – ಎಲ್ಲದರಲ್ಲೂ ಓದಬಹುದು. ಯಾವುದೇ PDF ಆಪ್ ಇದ್ದರೆ ಸಾಕು. ಬಸ್‌ನಲ್ಲಿ, ಮನೆಯಲ್ಲಿ, ದೇವಸ್ಥಾನದಲ್ಲಿ – ಎಲ್ಲಿ ಬೇಕಾದ್ರೂ ಓದಿ." },
  { q: "ಬೇರೆ ಏನಾದ್ರೂ ಚಾರ್ಜ್ ಇದೆಯಾ?", a: "ಇಲ್ಲ, ಬಿಲ್ಕುಲ್ ಇಲ್ಲ. ₹199 ಒಂದೇ ಸಲ ಕೊಟ್ಟರೆ ಸಾಕು. ಮತ್ತೆ ಯಾವ ಹಿಡನ್ ಚಾರ್ಜ್ ಇಲ್ಲ. ಜೀವನಪೂರ್ತಿ ನಿಮ್ಮ ಬಳಿ ಇರುತ್ತದೆ." },
  { q: "ಇದು ನಂಬಬಹುದಾದ ಗ್ರಂಥಗಳೇ?", a: "ಹೌದು. ವಿದ್ವಾಂಸರ ಸಹಾಯದಿಂದ ಅಧಿಕೃತ ಮೂಲಗಳಿಂದ ಸಂಗ್ರಹಿಸಿ, ನಿಖರವಾಗಿ ಕನ್ನಡಕ್ಕೆ ಅನುವಾದ ಮಾಡಿದ್ದೇವೆ. ಮೂಲ ಅರ್ಥ ಹಾಗೇ ಇದೆ." },
];

const TRUST_ITEMS = [
  { icon: Download, label: "ತಕ್ಷಣ ಡೌನ್\u200Cಲೋಡ್" },
  { icon: Shield, label: "ಜೀವನಪೂರ್ತಿ ಪ್ರವೇಶ" },
  { icon: Smartphone, label: "ಮೊಬೈಲ್\u200Cನಲ್ಲಿ ಸುಲಭವಾಗಿ ಓದಿ" },
  { icon: Lock, label: "ಸುರಕ್ಷಿತ ಪಾವತಿ" },
];

function useScrollAnimation() {
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => { entries.forEach((entry) => { if (entry.isIntersecting) entry.target.classList.add("visible"); }); },
      { threshold: 0.1 }
    );
    const elements = ref.current?.querySelectorAll(".animate-on-scroll");
    elements?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
  return ref;
}

function CTAButton({ className = "", testId = "cta-button", text = "₹199ಕ್ಕೆ ಈಗಲೇ ಪಡೆಯಿರಿ" }) {
  return (
    <a href={CTA_LINK} target="_blank" rel="noopener noreferrer" data-testid={testId}
      className={`inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#FF671F] to-[#E85D1A] text-white font-bold py-4 px-8 rounded-full text-base md:text-lg shadow-lg shadow-orange-500/30 cta-pulse body-font transition-all ${className}`}>
      <ChevronRight className="w-5 h-5" />
      {text}
    </a>
  );
}

function SectionHeading({ children }) {
  return (
    <div className="text-center animate-on-scroll">
      <h2 className="heading-font text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1C1917]">{children}</h2>
      <div className="spiritual-divider w-24 mx-auto mt-4" />
    </div>
  );
}

export default function LandingPage() {
  const pageRef = useScrollAnimation();

  return (
    <div ref={pageRef} className="min-h-screen bg-[#FFFCF9] mandala-bg">

      {/* ===== 1. HERO SECTION ===== */}
      <section data-testid="hero-section" className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1C1917] via-[#292524] to-[#1C1917]" />
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: `radial-gradient(circle at 30% 70%, rgba(212,175,55,0.3) 0%, transparent 50%), radial-gradient(circle at 70% 30%, rgba(255,103,31,0.2) 0%, transparent 50%)` }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="text-center lg:text-left">
              <h1 data-testid="hero-headline" className="heading-font text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-white leading-tight">
                <span className="gold-gradient-text">4 ವೇದಗಳು + 18 ಪುರಾಣಗಳು</span> ಈಗ ನಿಮ್ಮ ಮೊಬೈಲ್‌ನಲ್ಲಿ – ಸರಳ ಕನ್ನಡದಲ್ಲಿ!
              </h1>
              <p data-testid="hero-subheadline" className="mt-4 text-base md:text-lg text-gray-300 body-font leading-relaxed">
                ನಮ್ಮ ಹಿರಿಯರ ಜ್ಞಾನವನ್ನು ಈಗ ಮನೆಯಲ್ಲೇ ಕುಳಿತು ಓದಿ. ಕಷ್ಟದ ಸಂಸ್ಕೃತ ಬೇಡ – ಸುಲಭ ಕನ್ನಡದಲ್ಲಿ ಅರ್ಥ ಮಾಡಿಕೊಳ್ಳಿ. ನಿಮ್ಮ ಜೀವನದಲ್ಲಿ ಶಾಂತಿ ಮತ್ತು ಸಕಾರಾತ್ಮಕ ಶಕ್ತಿ ತರಲು ಸಹಾಯ ಮಾಡುತ್ತದೆ.
              </p>
              <p className="mt-2 text-sm text-[#D4AF37] body-font font-semibold">₹1999 ಮೌಲ್ಯದ ಜ್ಞಾನ – ಈಗ ಕೇವಲ ₹199ಕ್ಕೆ!</p>
              <div className="mt-5"><CTAButton testId="hero-cta-button" /></div>
              <CountdownTimer />
              <div className="mt-5 flex flex-wrap justify-center lg:justify-start gap-4">
                {TRUST_ITEMS.map((item, i) => (
                  <div key={i} className="flex items-center gap-1.5 text-gray-400 text-xs body-font"><item.icon className="w-3.5 h-3.5 text-[#D4AF37]" />{item.label}</div>
                ))}
              </div>
            </div>
            <div className="flex justify-center">
              <img srcSet={`${HERO_IMAGE_SM} 480w, ${HERO_IMAGE_MD} 768w, ${HERO_IMAGE_LG} 1024w`} sizes="(max-width: 640px) 480px, (max-width: 1024px) 768px, 1024px" src={HERO_IMAGE_MD} alt="Kannada Ved Puran Bundle" data-testid="hero-image" width="480" height="480" fetchpriority="high" decoding="async" className="w-64 sm:w-72 md:w-80 lg:w-full max-w-md rounded-2xl shadow-2xl shadow-orange-900/20" />
            </div>
          </div>
        </div>
      </section>

      {/* ===== 2. FEATURES ("ಈ ಸಂಗ್ರಹದಲ್ಲಿ ಏನನ್ನು ಪಡೆಯುವಿರಿ?") ===== */}
      <section data-testid="features-section" className="py-16 md:py-24" style={{ contentVisibility: 'auto', containIntrinsicSize: '0 600px' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading>ಇದರಲ್ಲಿ ನಿಮಗೆ ಏನೆಲ್ಲಾ ಸಿಗುತ್ತದೆ?</SectionHeading>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {FEATURES.map((feat, i) => (
              <div key={i} data-testid={`feature-card-${i}`} className="animate-on-scroll bg-white border border-[#F2E6DA] rounded-xl p-6 hover:shadow-lg hover:border-[#D4AF37]/40 transition-all duration-300" style={{ transitionDelay: `${i * 80}ms` }}>
                <div className="w-12 h-12 rounded-xl bg-[#FFF7ED] flex items-center justify-center mb-4"><feat.icon className="w-6 h-6 text-[#FF671F]" /></div>
                <h3 className="heading-font text-lg font-semibold text-[#1C1917]">{feat.title}</h3>
                <p className="text-sm text-[#57534E] mt-2 body-font leading-relaxed">{feat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 3. VEDAS TABLE ===== */}
      <section data-testid="vedas-section" className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading>ನಾಲ್ಕು ವೇದಗಳು</SectionHeading>
          <div className="mt-10 animate-on-scroll vedas-table rounded-xl overflow-hidden border border-[#F2E6DA]">
            <Table>
              <TableHeader><TableRow>
                <TableHead className="bg-[#FFF7ED] text-[#C2410C] heading-font font-semibold text-base py-3.5 px-4">ವೇದದ ಹೆಸರು</TableHead>
                <TableHead className="bg-[#FFF7ED] text-[#C2410C] heading-font font-semibold text-base py-3.5 px-4">ಸಂಕ್ಷಿಪ್ತ ವಿವರಣೆ</TableHead>
              </TableRow></TableHeader>
              <TableBody>{VEDAS.map((v, i) => (
                <TableRow key={i} data-testid={`veda-row-${i}`}>
                  <TableCell className="font-semibold body-font py-3.5 px-4 text-[#1C1917]">{v.name}</TableCell>
                  <TableCell className="body-font py-3.5 px-4 text-[#57534E]">{v.desc}</TableCell>
                </TableRow>
              ))}</TableBody>
            </Table>
          </div>
        </div>
      </section>

      {/* ===== 4. PURANAS TABLE ===== */}
      <section data-testid="puranas-section" className="py-16 md:py-24" style={{ contentVisibility: 'auto', containIntrinsicSize: '0 800px' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading>ಹದಿನೆಂಟು ಪುರಾಣಗಳು</SectionHeading>
          <div className="mt-10 animate-on-scroll vedas-table rounded-xl overflow-hidden border border-[#F2E6DA] bg-white">
            <Table>
              <TableHeader><TableRow>
                <TableHead className="bg-[#FFF7ED] text-[#C2410C] heading-font font-semibold text-base py-3.5 px-4 w-12">#</TableHead>
                <TableHead className="bg-[#FFF7ED] text-[#C2410C] heading-font font-semibold text-base py-3.5 px-4">ಪುರಾಣದ ಹೆಸರು</TableHead>
                <TableHead className="bg-[#FFF7ED] text-[#C2410C] heading-font font-semibold text-base py-3.5 px-4">ಸಂಕ್ಷಿಪ್ತ ವಿವರಣೆ</TableHead>
              </TableRow></TableHeader>
              <TableBody>{PURANAS.map((p, i) => (
                <TableRow key={i} data-testid={`purana-row-${i}`}>
                  <TableCell className="font-semibold body-font py-3 px-4 text-[#D4AF37]">{i + 1}</TableCell>
                  <TableCell className="font-semibold body-font py-3 px-4 text-[#1C1917]">{p.name}</TableCell>
                  <TableCell className="body-font py-3 px-4 text-[#57534E]">{p.desc}</TableCell>
                </TableRow>
              ))}</TableBody>
            </Table>
          </div>
        </div>
      </section>

      {/* ===== 5. MID-PAGE CTA ("ಈ ದಿವ್ಯ ಜ್ಞಾನವನ್ನು ಈಗಲೇ ಪಡೆಯಿರಿ") ===== */}
      <section data-testid="mid-cta-section" className="py-16 md:py-20 bg-gradient-to-br from-[#FFF7ED] to-[#FFFCF9]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="animate-on-scroll">
            <h2 className="heading-font text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1C1917]">ಓದದೇ ಕೇಳಿ – ವೇದಗಳ ಜ್ಞಾನ ನಿಮ್ಮ ಕೈಯಲ್ಲಿ</h2>
            <div className="spiritual-divider w-24 mx-auto mt-4" />
          </div>
          <p className="animate-on-scroll mt-6 body-font text-[#57534E] leading-relaxed text-base md:text-lg">
            ₹1999 ಮೌಲ್ಯದ ಈ ಅಮೂಲ್ಯ ಜ್ಞಾನ ಈಗ ಕೇವಲ ₹199ಕ್ಕೆ. ವ್ಯಸ್ತ ಜೀವನದಲ್ಲಿಯೂ ಸುಲಭವಾಗಿ ಉಪಯೋಗಿಸಬಹುದು. ಇದು ಕೇವಲ ಪುಸ್ತಕ ಅಲ್ಲ – ನಮ್ಮ ಹಿರಿಯರ ಆಶೀರ್ವಾದ.
          </p>
          <p className="animate-on-scroll mt-2 text-sm text-red-500 font-semibold body-font">ಸೀಮಿತ ಸಮಯದ ಆಫರ್ – ಈ ಬೆಲೆ ಇಂದು ಮಾತ್ರ!</p>
          <div className="animate-on-scroll mt-6"><CTAButton testId="mid-cta-button" text="₹199ಕ್ಕೆ ಇಂದೇ ಡೌನ್‌ಲೋಡ್ ಮಾಡಿ" /></div>
        </div>
      </section>

      {/* ===== 6. PRODUCT DESCRIPTION ("ವೇದಗಳು ಮತ್ತು ಪುರಾಣಗಳು – ನಿಮ್ಮ ಮನೆಗಳಲ್ಲೇ...") ===== */}
      <section data-testid="product-description-section" className="py-16 md:py-24 bg-white" style={{ contentVisibility: 'auto', containIntrinsicSize: '0 500px' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll text-center">
            <h2 className="heading-font text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1C1917] leading-tight">
              ನಮ್ಮ ಅಜ್ಜ-ಅಜ್ಜಿಯರ ಜ್ಞಾನ ಈಗ ನಿಮ್ಮ ಫೋನ್‌ನಲ್ಲಿ!
            </h2>
            <div className="spiritual-divider w-24 mx-auto mt-4" />
          </div>
          <div className="animate-on-scroll mt-8 space-y-5 body-font text-[#57534E] leading-relaxed text-base">
            <p>ನಮ್ಮ ಹಿರಿಯರು ಸಾವಿರಾರು ವರ್ಷದಿಂದ ಕಾಪಾಡಿಟ್ಟ ಜ್ಞಾನ ಇದು. ಹಿಂದೆ ಇದನ್ನ ಕಲಿಯಬೇಕಾದ್ರೆ ಗುರುಕುಲಕ್ಕೆ ಹೋಗಬೇಕಿತ್ತು. ಈಗ ಅದೆಲ್ಲಾ ನಿಮ್ಮ ಫೋನ್‌ನಲ್ಲೇ ಇದೆ.</p>
            <p>ನಮ್ಮ <strong className="text-[#1C1917]">ವೇದ-ಪುರಾಣ ಸಂಗ್ರಹ</strong> ಸುಲಭ ಕನ್ನಡದಲ್ಲಿ ಇದೆ. ಕಷ್ಟದ ಸಂಸ್ಕೃತ ಇಲ್ಲ. ಮನೆಯಲ್ಲಿ ಹೆಂಡ್ತಿ-ಮಕ್ಕಳೆಲ್ಲ ಸೇರಿ ಓದಬಹುದು. ಕುಟುಂಬದಲ್ಲಿ ಒಳ್ಳೆಯ ಸಂಸ್ಕಾರ ಬೆಳೆಯುತ್ತೆ.</p>
          </div>
          <div className="animate-on-scroll text-center mt-8"><CTAButton testId="product-desc-cta" text="₹199ಕ್ಕೆ ಈಗಲೇ ಪಡೆಯಿರಿ" /></div>
        </div>
      </section>

      {/* ===== 7. UNIQUE FEATURES ("ವೈಶಿಷ್ಟ್ಯಗಳು") ===== */}
      <section data-testid="unique-features-section" className="py-16 md:py-24" style={{ contentVisibility: 'auto', containIntrinsicSize: '0 600px' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading>ಯಾಕೆ ಈ ಸಂಗ್ರಹ ಬೇರೆಯವರಿಗಿಂತ ಬೆಸ್ಟ್?</SectionHeading>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12">
            {UNIQUE_FEATURES.map((f, i) => (
              <div key={i} data-testid={`unique-feature-${i}`} className="animate-on-scroll bg-white border border-[#F2E6DA] rounded-xl p-6 hover:shadow-lg transition-all duration-300" style={{ transitionDelay: `${i * 80}ms` }}>
                <div className="w-12 h-12 rounded-xl bg-[#FFF7ED] flex items-center justify-center mb-4"><f.icon className="w-6 h-6 text-[#FF671F]" /></div>
                <h3 className="heading-font text-lg font-semibold text-[#1C1917]">{f.title}</h3>
                <p className="text-sm text-[#57534E] mt-2 body-font leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
          <p className="animate-on-scroll mt-10 body-font text-[#57534E] leading-relaxed text-center max-w-3xl mx-auto">
            ಇದು ಬರೀ ಪುಸ್ತಕ ಅಲ್ಲ ಗುರು – ನಿಮ್ಮ ಆಧ್ಯಾತ್ಮಿಕ ಬದುಕಿನ ಜೊತೆಗಾರ. <strong className="text-[#1C1917]">ಧರ್ಮ, ಪೂಜೆ, ಆಚರಣೆ, ಕಥೆಗಳು</strong> – ಎಲ್ಲಾ ಒಂದೇ ಕಡೆ ಸಿಗುತ್ತೆ.
          </p>
        </div>
      </section>

      {/* ===== 8. PROBLEMS SOLVED ("ಪರಿಹಾರ ಸಿಗುವ ಸಮಸ್ಯೆಗಳು") ===== */}
      <section data-testid="problems-section" className="py-16 md:py-24 bg-white" style={{ contentVisibility: 'auto', containIntrinsicSize: '0 600px' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading>ಈ ಸಮಸ್ಯೆ ನಿಮಗೂ ಇದೆಯಾ?</SectionHeading>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mt-12 items-center">
            <div className="animate-on-scroll">
              <img src={PROBLEMS_IMAGE} alt="ಪ್ರಾಚೀನ ಗ್ರಂಥಗಳು" loading="lazy" className="rounded-2xl shadow-lg w-full max-w-lg mx-auto" />
            </div>
            <div className="space-y-6">
              {PROBLEMS.map((p, i) => (
                <div key={i} data-testid={`problem-${i}`} className="animate-on-scroll flex gap-4" style={{ transitionDelay: `${i * 100}ms` }}>
                  <div className="w-12 h-12 rounded-xl bg-[#FFF7ED] flex items-center justify-center flex-shrink-0"><p.icon className="w-6 h-6 text-[#FF671F]" /></div>
                  <div>
                    <h3 className="heading-font text-lg font-semibold text-[#1C1917]">{p.title}</h3>
                    <p className="text-sm text-[#57534E] mt-1 body-font leading-relaxed">{p.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <p className="animate-on-scroll mt-10 body-font text-[#57534E] leading-relaxed text-center max-w-3xl mx-auto">
            ಈ ಎಲ್ಲಾ ಸಮಸ್ಯೆಗಳಿಗೆ ಒಂದೇ ಉತ್ತರ – ನಮ್ಮ ಸರಳ ಕನ್ನಡ ವೇದ-ಪುರಾಣ ಸಂಗ್ರಹ. ಮನೆಯಲ್ಲೇ ಕುಳಿತು ಫೋನ್‌ನಲ್ಲಿ ಓದಿ!
          </p>
        </div>
      </section>

      {/* ===== 9. BENEFITS ("ಪ್ರಯೋಜನಗಳು") ===== */}
      <section data-testid="benefits-section" className="py-16 md:py-24" style={{ contentVisibility: 'auto', containIntrinsicSize: '0 600px' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading>ಇದನ್ನ ಓದಿದ್ರೆ ಏನು ಲಾಭ?</SectionHeading>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {BENEFITS.map((b, i) => (
              <div key={i} data-testid={`benefit-${i}`} className="animate-on-scroll bg-white border border-[#F2E6DA] rounded-xl p-6 hover:shadow-lg transition-all duration-300" style={{ transitionDelay: `${i * 80}ms` }}>
                <div className="w-12 h-12 rounded-xl bg-[#FFF7ED] flex items-center justify-center mb-4"><b.icon className="w-6 h-6 text-[#D4AF37]" /></div>
                <h3 className="heading-font text-lg font-semibold text-[#1C1917]">{b.title}</h3>
                <p className="text-sm text-[#57534E] mt-2 body-font leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
          <p className="animate-on-scroll mt-10 body-font text-[#57534E] leading-relaxed text-center max-w-3xl mx-auto">
            ಬರೀ ಓದೋದು ಅಲ್ಲ – <strong className="text-[#1C1917]">ಬದುಕನ್ನೇ ಬದಲಾಯಿಸುತ್ತೆ</strong>. ಪ್ರತಿ ದಿನ 10-15 ನಿಮಿಷ ಓದಿದ್ರೆ ಸಾಕು, ಲೈಫ್‌ನಲ್ಲಿ ಫರ್ಕ್ ಕಾಣಿಸುತ್ತೆ.
          </p>
        </div>
      </section>

      {/* ===== 10. DETAILED BONUS ("ವಿಶೇಷ ಬೋನಸ್") ===== */}
      <section data-testid="bonus-section" className="py-16 md:py-24 bg-white" style={{ contentVisibility: 'auto', containIntrinsicSize: '0 500px' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading>FREE ಬೋನಸ್ – ಹೆಚ್ಚುವರಿ ಚಾರ್ಜ್ ಇಲ್ಲ!</SectionHeading>
          <div className="mt-12 space-y-8">
            <div className="animate-on-scroll bg-[#FFFCF9] border border-[#F2E6DA] rounded-xl p-6 md:p-8" data-testid="bonus-vastu">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#FFF7ED] flex items-center justify-center"><Gift className="w-5 h-5 text-[#FF671F]" /></div>
                <h3 className="heading-font text-lg font-bold text-[#1C1917]">ವಾಸ್ತು ಪುಸ್ತಕ PDF – ಮನೆ ಸರಿ ಇದ್ರೆ ಎಲ್ಲಾ ಸರಿ!</h3>
              </div>
              <ul className="space-y-2 body-font text-[#57534E] text-sm leading-relaxed ml-13">
                <li className="flex gap-2"><ChevronRight className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />ವಾಸ್ತು ಪ್ರಕಾರ ಮನೆ ಸರಿ ಮಾಡಿದ್ರೆ ಕುಟುಂಬದಲ್ಲಿ ಶಾಂತಿ, ಆರೋಗ್ಯ ಎಲ್ಲಾ ಸರಿ ಆಗುತ್ತೆ.</li>
                <li className="flex gap-2"><ChevronRight className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />ಹಣ ಬರೋ ದಿಕ್ಕು, ಪೂಜಾ ಕೋಣೆ ಎಲ್ಲಿ ಇರಬೇಕು – ಎಲ್ಲಾ ಟಿಪ್ಸ್ ಇದರಲ್ಲಿ ಇವೆ.</li>
              </ul>
            </div>
            <div className="animate-on-scroll bg-[#FFFCF9] border border-[#F2E6DA] rounded-xl p-6 md:p-8" data-testid="bonus-yoga">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#FFF7ED] flex items-center justify-center"><Music className="w-5 h-5 text-[#FF671F]" /></div>
                <h3 className="heading-font text-lg font-bold text-[#1C1917]">ಯೋಗಾಸನ PDF – ದೇಹ ಫಿಟ್, ಮನಸ್ಸು ಫಿಟ್!</h3>
              </div>
              <p className="body-font text-[#57534E] text-sm leading-relaxed ml-13">
                ಒಳಗಿನಿಂದ ಆಯುರ್ವೇದ ಸರಿ ಮಾಡುತ್ತೆ, ಹೊರಗಿನಿಂದ ಯೋಗ ಸರಿ ಮಾಡುತ್ತೆ. ಪ್ರತಿ ದಿನ 15 ನಿಮಿಷ ಸಾಕು!
              </p>
            </div>
          </div>
          <p className="animate-on-scroll mt-8 body-font text-[#57534E] leading-relaxed text-center">
            ಎಲ್ಲಾ ಬೋನಸ್ <strong className="text-[#1C1917]">ಫ್ರೀ</strong> – ₹199ರಲ್ಲೇ ಎಲ್ಲಾ ಸೇರಿದೆ. ಬೇರೆ ಯಾವ ಚಾರ್ಜ್ ಇಲ್ಲ!
          </p>
        </div>
      </section>

      {/* ===== 11. TARGET AUDIENCE ("ಯಾರಿಗೆ ಸೂಕ್ತ") ===== */}
      <section data-testid="audience-section" className="py-16 md:py-24" style={{ contentVisibility: 'auto', containIntrinsicSize: '0 600px' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading>ಇದು ಯಾರಿಗೆ?</SectionHeading>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mt-12 items-center">
            <div className="space-y-6">
              {TARGET_AUDIENCE.map((a, i) => (
                <div key={i} data-testid={`audience-${i}`} className="animate-on-scroll flex gap-4" style={{ transitionDelay: `${i * 100}ms` }}>
                  <div className="w-12 h-12 rounded-xl bg-[#FFF7ED] flex items-center justify-center flex-shrink-0"><a.icon className="w-6 h-6 text-[#FF671F]" /></div>
                  <div>
                    <h3 className="heading-font text-lg font-semibold text-[#1C1917]">{a.title}</h3>
                    <p className="text-sm text-[#57534E] mt-1 body-font leading-relaxed">{a.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="animate-on-scroll">
              <img src={AUDIENCE_IMAGE} alt="ಸಂಸ್ಕೃತಿ ಮತ್ತು ಸಂಪ್ರದಾಯ" loading="lazy" className="rounded-2xl shadow-lg w-full max-w-lg mx-auto" />
            </div>
          </div>
          <p className="animate-on-scroll mt-10 body-font text-[#57534E] leading-relaxed text-center max-w-3xl mx-auto">
            ನಮ್ಮ ಸಂಸ್ಕೃತಿ ಬಗ್ಗೆ ಹೆಮ್ಮೆ ಇರೋ, ಕುಟುಂಬದಲ್ಲಿ ಒಳ್ಳೆಯ ವ್ಯಾಲ್ಯೂಸ್ ಕಟ್ಟಬೇಕು ಅಂತಿರೋ <strong className="text-[#1C1917]">ಎಲ್ಲಾ ಸ್ವಾಭಿಮಾನಿ ಕನ್ನಡಿಗರಿಗೆ</strong> ಇದು.
          </p>
        </div>
      </section>

      {/* ===== 12. TESTIMONIALS ===== */}
      <section data-testid="testimonials-section" className="py-16 md:py-24 bg-white" style={{ contentVisibility: 'auto', containIntrinsicSize: '0 500px' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading>ತಗೊಂಡವರು ಏನಂತಾರೆ ನೋಡಿ</SectionHeading>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} data-testid={`testimonial-${i}`} className="animate-on-scroll bg-[#FFFCF9] border border-[#F2E6DA] rounded-xl p-6" style={{ transitionDelay: `${i * 100}ms` }}>
                <div className="flex items-center gap-1 mb-3">{[...Array(5)].map((_, j) => (<Star key={j} className="w-4 h-4 fill-[#D4AF37] text-[#D4AF37]" />))}</div>
                <p className="body-font text-[#57534E] leading-relaxed italic">"{t.text}"</p>
                <div className="mt-4 flex items-center gap-2">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#FF671F] to-[#D4AF37] flex items-center justify-center text-white text-sm font-bold heading-font">{t.name[0]}</div>
                  <div>
                    <p className="text-sm font-semibold text-[#1C1917] body-font">{t.name}, {t.age}</p>
                    <p className="text-xs text-[#57534E] body-font">{t.city}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="animate-on-scroll mt-10 body-font text-[#57534E] leading-relaxed text-center max-w-3xl mx-auto">
            ಇದು <strong className="text-[#1C1917]">ಬರೀ ಪುಸ್ತಕ ಅಲ್ಲ</strong> – ಜನರ ಬದುಕನ್ನೇ ಬದಲಾಯಿಸಿದೆ. ನೀವೂ ಟ್ರೈ ಮಾಡಿ ನೋಡಿ!
          </p>
        </div>
      </section>

      {/* ===== 13. FAQ ===== */}
      <section data-testid="faq-section" className="py-16 md:py-24" style={{ contentVisibility: 'auto', containIntrinsicSize: '0 500px' }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading>ನಿಮ್ಮ ಡೌಟ್ಸ್ – ನಮ್ಮ ಉತ್ತರ</SectionHeading>
          <div className="mt-10 animate-on-scroll">
            <Accordion type="single" collapsible className="space-y-2">
              {FAQS.map((faq, i) => (
                <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`} className="bg-white border border-[#F2E6DA] rounded-xl px-5 data-[state=open]:border-[#D4AF37]/50">
                  <AccordionTrigger className="heading-font text-base text-[#1C1917] hover:no-underline py-4">{faq.q}</AccordionTrigger>
                  <AccordionContent className="body-font text-[#57534E] leading-relaxed">{faq.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
          <div className="animate-on-scroll mt-8 text-center body-font text-sm text-[#57534E]">
            ಇನ್ನೇನಾದ್ರೂ ಡೌಟ್ ಇದ್ರೆ ಮೇಲ್ ಮಾಡಿ:{" "}
            <span className="text-red-500 font-semibold">support@vedicbookstore.in</span>
          </div>
        </div>
      </section>

      {/* ===== 14. PRICING ===== */}
      <section data-testid="pricing-section" className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1C1917] via-[#292524] to-[#1C1917]" />
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: `radial-gradient(circle at 50% 50%, rgba(212,175,55,0.3) 0%, transparent 60%)` }} />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="animate-on-scroll"><h2 className="heading-font text-2xl sm:text-3xl lg:text-4xl font-bold text-white"><span className="text-red-400">ಸೀಮಿತ ಸಮಯದ ಕೊಡುಗೆ</span></h2><div className="spiritual-divider w-24 mx-auto mt-4" /></div>
          <div className="animate-on-scroll mt-10 grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8">
              <h3 className="heading-font text-xl text-white font-semibold mb-4">ಬೆಲೆ</h3>
              <p className="text-gray-400 line-through text-xl body-font">₹1999</p>
              <p className="text-5xl font-black text-red-400 heading-font mt-2">₹199</p>
              <p className="text-sm text-gray-300 mt-3 body-font">₹1800 ಉಳಿಸಿ (90% ರಿಯಾಯಿತಿ)</p>
              <p className="mt-3 text-xs text-red-300 font-semibold body-font">ಈ ಆಫರ್ ಇಂದು ಮಾತ್ರ ಲಭ್ಯ!</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 text-left">
              <h3 className="heading-font text-xl text-white font-semibold mb-4">ನೀವು ಏನನ್ನು ಪಡೆಯುತ್ತೀರಿ</h3>
              <ul className="space-y-2.5">
                {["4 ಪ್ರಮುಖ ವೇದಗಳು (ಸರಳ ಕನ್ನಡದಲ್ಲಿ) PDF","18 ಮುಖ್ಯ ಪುರಾಣಗಳು (ಸರಳ ಕನ್ನಡದಲ್ಲಿ) PDF","ಪ್ರಮುಖ ಸೂಕ್ತಗಳು ಮತ್ತು ಕಥೆಗಳು","ಪ್ರಶ್ನೋತ್ತರ ಅಭ್ಯಾಸ ಹಾಳೆಗಳು","ಜನ್ಮಕುಂಡಲಿ ಸಂಬಂಧಿತ ವಿಶೇಷ ಮಾಹಿತಿ"].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-gray-300 body-font text-sm"><ChevronRight className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />{item}</li>
                ))}
              </ul>
            </div>
          </div>
          <div className="animate-on-scroll mt-10 p-4 bg-red-500/20 border border-red-400/30 rounded-xl">
            <p className="text-white body-font font-semibold text-sm md:text-base">ಸೀಮಿತ ಸಮಯದ ಆಫರ್! ಇಂದೇ ₹199ಕ್ಕೆ ಪಡೆಯಿರಿ – ನಾಳೆ ಈ ಬೆಲೆ ಇರುವುದಿಲ್ಲ.</p>
          </div>
          <div className="animate-on-scroll mt-8"><CTAButton testId="pricing-cta-button" text="₹199ಕ್ಕೆ ಈಗಲೇ ಪಡೆಯಿರಿ" className="text-lg px-10 py-5" /></div>
          <p className="text-gray-400 text-sm mt-4 body-font">ತಕ್ಷಣ ಡೌನ್‌ಲೋಡ್ | ಜೀವನಪೂರ್ತಿ ಪ್ರವೇಶ | ಯಾವುದೇ ಹಿಡನ್ ಚಾರ್ಜ್ ಇಲ್ಲ</p>
        </div>
      </section>

      {/* ===== 15. TRUST BADGES ===== */}
      <section data-testid="trust-section" className="py-12 md:py-16 bg-[#FFF7ED]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {TRUST_ITEMS.map((item, i) => (
              <div key={i} data-testid={`trust-badge-${i}`} className="animate-on-scroll flex flex-col items-center text-center gap-3" style={{ transitionDelay: `${i * 100}ms` }}>
                <div className="w-14 h-14 rounded-full bg-white shadow-md flex items-center justify-center"><item.icon className="w-6 h-6 text-[#FF671F]" /></div>
                <p className="text-sm font-semibold text-[#1C1917] body-font">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 16. FINAL CTA ===== */}
      <section data-testid="final-cta-section" className="py-16 md:py-24 bg-gradient-to-br from-[#FFF7ED] to-[#FFFCF9]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="animate-on-scroll">
            <h2 className="heading-font text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1C1917]">
              <span className="text-red-600">ನಿಮ್ಮ ಆಧ್ಯಾತ್ಮಿಕ ಪ್ರಯಾಣ</span> ಇಲ್ಲಿಂದ ಶುರು!
            </h2>
          </div>
          <div className="animate-on-scroll mt-6">
            <p className="body-font text-base md:text-lg text-[#57534E] leading-relaxed max-w-2xl mx-auto">
              ವೇದ-ಪುರಾಣಗಳ ಜ್ಞಾನವನ್ನು ನಿಮ್ಮ ಕುಟುಂಬದೊಂದಿಗೆ ಹಂಚಿಕೊಳ್ಳಿ. ನಿಮ್ಮ ಮಕ್ಕಳಿಗೆ ನಮ್ಮ ಸಂಸ್ಕೃತಿ ಬಗ್ಗೆ ಹೆಮ್ಮೆ ಹುಟ್ಟಿಸಿ. ವ್ಯಸ್ತ ಜೀವನದಲ್ಲಿಯೂ ಮೊಬೈಲ್‌ನಲ್ಲಿ ಸುಲಭವಾಗಿ ಓದಬಹುದು.
            </p>
            <p className="body-font text-sm md:text-base text-[#57534E] leading-relaxed max-w-2xl mx-auto mt-4">
              ₹1999 ಮೌಲ್ಯದ ಸಂಪೂರ್ಣ ಸಂಗ್ರಹ – ಈಗ ಕೇವಲ ₹199ಕ್ಕೆ. ಈ ಬೆಲೆ ಎಷ್ಟು ದಿನ ಇರುತ್ತೋ ಗೊತ್ತಿಲ್ಲ!
            </p>
          </div>
          <div className="animate-on-scroll mt-8"><CTAButton testId="final-cta-button" text="₹199ಕ್ಕೆ ಇಂದೇ ಡೌನ್‌ಲೋಡ್ ಮಾಡಿ" className="text-lg px-10 py-5" /></div>
          <p className="animate-on-scroll text-sm text-[#57534E] mt-6 body-font">ತಕ್ಷಣ ಡೌನ್‌ಲೋಡ್ | ಜೀವನಪೂರ್ತಿ ಪ್ರವೇಶ | ಸುರಕ್ಷಿತ ಪಾವತಿ</p>
        </div>
      </section>

      {/* ===== 17. FOOTER ===== */}
      <footer data-testid="footer" className="py-8 bg-[#1C1917] text-center">
        <div className="max-w-4xl mx-auto px-4">
          <p className="text-gray-400 text-sm body-font">
            ಏನಾದ್ರೂ ಪ್ರಾಬ್ಲಮ್ ಇದ್ರೆ ಮೇಲ್ ಮಾಡಿ:{" "}
            <span className="text-red-400 font-semibold">support@vedicbookstore.in</span>
          </p>
        </div>
      </footer>

      {/* ===== OVERLAYS (lazy loaded) ===== */}
      <Suspense fallback={null}>
        <StickyBuyButton />
        <SocialProofPopup />
        <ExitIntentPopup />
      </Suspense>
      <div className="h-16" />
    </div>
  );
}
